import * as React from "react";
import axios from "axios";
import { Link as RouterLink, useParams } from "react-router-dom";
import { useState, useEffect, useCallback } from "react";
import moment from "moment/moment";
import "./match.css";
import { appConstants } from "../_constants/app.constants";
import { saveTournamentShields } from "../_utils/shieldUtils";
import {
  buildImageMap,
  collectImagesFromMatches,
  dedupeTeamImages,
  patchTeamShield,
  readCachedTournamentImages,
  resolveTournamentIdForViewer,
} from "../_utils/tournamentViewUtils";
import { useTournamentAccess } from "../hooks/useTournamentAccess";
import { useTournamentPageView } from "../analytics/useTournamentPageView";
import { Button, EmptyState, LoadingState, Toast } from "../ui";

function normalizeFieldName(fieldName) {
  if (fieldName == null || String(fieldName).trim() === "") return "Sin campo asignado";
  return String(fieldName).trim();
}

function teamInitial(name) {
  if (!name || typeof name !== "string" || name.length === 0) return "?";
  return name.charAt(0).toUpperCase();
}

const MatchRow = ({ match, index }) => {
  const getTeamClass = (isLocalTeam) => {
    if (!match.completed) return "";
    if (match.localGoals > match.foreignGoals) return isLocalTeam ? "winner" : "loser";
    if (match.localGoals < match.foreignGoals) return isLocalTeam ? "loser" : "winner";
    return "draw";
  };

  const localClass = getTeamClass(true);
  const foreignClass = getTeamClass(false);
  const localName = match.localTeam?.name ?? "";
  const foreignName = match.foreignTeam?.name ?? "";

  return (
    <tr>
      <td className="time-cell">
        <span className={match.isActive ? "match-time match-live" : "match-time"}>{moment(match.dayHour).format("HH:mm")}</span>
      </td>
      <td className="team-logo-cell">
        {match.localTeam?.shield ? (
          <img
            className="team-logo"
            src={match.localTeam.shield}
            alt={`Escudo ${localName}`}
            loading="lazy"
            onError={(e) => {
              e.target.style.display = "none";
            }}
          />
        ) : (
          <div className="team-logo-placeholder" title={`Sin escudo: ${localName}`}>
            {teamInitial(localName)}
          </div>
        )}
      </td>
      <td className="local-team" title={localName}>
        <span className={localClass}>{localName}</span>
      </td>
      <td className="score-cell">
        <span className="match-result">
          <span className={`${localClass} ${match.isActive ? "match-live" : ""}`}>{match.localGoals ?? 0}</span> -{" "}
          <span className={`${foreignClass} ${match.isActive ? "match-live" : ""}`}>{match.foreignGoals ?? 0}</span>
        </span>
      </td>
      <td className="visitor-team" title={foreignName}>
        <span className={foreignClass}>{foreignName}</span>
      </td>
      <td className="team-logo-cell">
        {match.foreignTeam?.shield ? (
          <img
            className="team-logo"
            src={match.foreignTeam.shield}
            alt={`Escudo ${foreignName}`}
            loading="lazy"
            onError={(e) => {
              e.target.style.display = "none";
            }}
          />
        ) : (
          <div className="team-logo-placeholder" title={`Sin escudo: ${foreignName}`}>
            {teamInitial(foreignName)}
          </div>
        )}
      </td>
    </tr>
  );
};

/**
 * Fila placeholder que se renderiza en un campo cuando OTRO campo tiene un
 * partido a esa hora pero éste no. Sirve solo para alinear horizontalmente
 * los horarios entre columnas. Mantiene la misma altura que una fila normal.
 */
const EmptyMatchRow = ({ time }) => (
  <tr className="empty-row" aria-hidden="true">
    <td className="time-cell">
      <span className="match-time" style={{ opacity: 0.35 }}>{time}</span>
    </td>
    <td className="team-logo-cell">
      <div className="team-logo-placeholder" style={{ visibility: "hidden" }}>
        &nbsp;
      </div>
    </td>
    <td className="local-team" />
    <td className="score-cell">
      <span className="match-result" style={{ opacity: 0.3 }}>—</span>
    </td>
    <td className="visitor-team" />
    <td className="team-logo-cell" />
  </tr>
);

export default function Matches() {
  const { tournamentId } = useParams();
  const [tId, setTournamentId] = useState(tournamentId ?? null);
  const { canManage, loaded: accessLoaded } = useTournamentAccess(tId);
  const [matches, setMatches] = useState([]);
  const [fields, setFields] = useState([]);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState("");
  const [windowWidth, setWindowWidth] = useState(window.innerWidth);
  useTournamentPageView(tId, "matches");

  useEffect(() => {
    let active = true;
    const resolveTournament = async () => {
      setLoading(true);
      const resolvedId = await resolveTournamentIdForViewer(tournamentId);
      if (!active) return;
      setTournamentId(resolvedId);
      if (!resolvedId) {
        setLoadError("");
        setMatches([]);
        setFields([]);
        setLoading(false);
      }
    };
    resolveTournament();
    return () => {
      active = false;
    };
  }, [tournamentId]);

  useEffect(() => {
    const handleResize = () => setWindowWidth(window.innerWidth);
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  const hydrateMatches = useCallback(
    (fetchedMatches) => {
      const source = Array.isArray(fetchedMatches) ? fetchedMatches : [];
      const cachedImages = readCachedTournamentImages(tId);
      const freshImages = collectImagesFromMatches(source);
      const mergedImages = dedupeTeamImages([...cachedImages, ...freshImages]);

      if (mergedImages.length > 0 && tId) saveTournamentShields(tId, mergedImages);

      const imageMap = buildImageMap(mergedImages);
      const hydrated = source.map((match) => ({
        ...match,
        localTeam: patchTeamShield(match.localTeam, imageMap),
        foreignTeam: patchTeamShield(match.foreignTeam, imageMap),
        winnerTeam: patchTeamShield(match.winnerTeam, imageMap),
      }));

      const fieldKeys = [...new Set(hydrated.map((match) => normalizeFieldName(match.fieldName)))];
      fieldKeys.sort((a, b) => {
        if (a === "Sin campo asignado") return 1;
        if (b === "Sin campo asignado") return -1;
        return String(a).localeCompare(String(b), "es");
      });

      setMatches(hydrated);
      setFields(fieldKeys);
      setLoading(false);
    },
    [tId]
  );

  useEffect(() => {
    if (!tId) return undefined;

    let cancelled = false;
    const fetchMatches = async () => {
      try {
        setLoadError("");
        const res = await axios.get(`${appConstants.API_URL}/api/tournamentmatches/${tId}`);
        if (!cancelled) hydrateMatches(res.data);
      } catch (err) {
        if (cancelled) return;
        const status = err?.response?.status;
        if (status === 403 || status === 404) setLoadError("No tienes acceso público a este torneo.");
        else setLoadError("No se pudieron cargar los partidos.");
        setMatches([]);
        setFields([]);
        setLoading(false);
      }
    };

    fetchMatches();
    const interval = setInterval(fetchMatches, 10000);
    return () => {
      cancelled = true;
      clearInterval(interval);
    };
  }, [hydrateMatches, tId]);

  if (loading && matches.length === 0) return <LoadingState />;

  if (!loading && !tId) {
    return (
      <EmptyState
        title="Falta el torneo en la URL"
        description="Abre esta vista desde el menú del torneo; la ruta debe incluir el identificador del torneo. No se elige ningún torneo automáticamente al abrir /matches sin id."
      />
    );
  }

  if (!loading && loadError) {
    return (
      <div className="mx-auto max-w-xl p-4">
        <Toast kind="warning">{loadError}</Toast>
      </div>
    );
  }

  if (!loading && matches.length === 0) {
    const adminCopy = accessLoaded && canManage;
    return (
      <EmptyState
        title="No hay partidos en este torneo"
        description={
          adminCopy
            ? "Genera el calendario de grupos desde la configuración o revisa que el grupo tenga al menos dos equipos."
            : "Aún no hay partidos programados. Vuelve más tarde o consulta al organizador."
        }
        action={
          adminCopy && tId ? (
            <RouterLink to={`/tournamentactions/${tId}/setup#calendario`}>
              <Button variant="gold">Ir al calendario</Button>
            </RouterLink>
          ) : null
        }
      />
    );
  }

  const getFieldColumns = () => {
    const count = fields.length;
    if (windowWidth >= 2560) return count === 1 ? "grid-cols-1" : count === 2 ? "grid-cols-2" : count === 3 ? "grid-cols-3" : count === 4 ? "grid-cols-4" : "grid-cols-6";
    if (windowWidth >= 1920) return count === 1 ? "grid-cols-1" : count === 2 ? "grid-cols-2" : count === 3 ? "grid-cols-3" : count === 4 ? "grid-cols-4" : "grid-cols-6";
    if (windowWidth >= 1280) return count === 1 ? "grid-cols-1" : count === 2 ? "grid-cols-2" : count === 3 ? "grid-cols-3" : "grid-cols-4";
    return "grid-cols-1 md:grid-cols-2";
  };

  // Unión de horas de TODOS los campos para alinear las filas horizontalmente:
  // si un campo no tiene partido a una hora que sí existe en otro, ahí va una
  // fila vacía con esa hora, así los partidos quedan en la misma "línea".
  const timeKey = (d) => moment(d).format("YYYY-MM-DD HH:mm");
  const allTimes = [...new Set(matches.map((m) => timeKey(m.dayHour)))].sort();
  const matchesByFieldAndTime = new Map(
    fields.map((field) => [
      field,
      new Map(
        matches
          .filter((m) => normalizeFieldName(m.fieldName) === field)
          .map((m) => [timeKey(m.dayHour), m])
      ),
    ])
  );

  return (
    <div className="matches-container min-h-screen w-full p-1 sm:p-2">
      <div className={`grid gap-1.5 ${getFieldColumns()}`}>
        {fields.map((field, fieldIndex) => {
          const byTime = matchesByFieldAndTime.get(field) || new Map();
          return (
            <section key={fieldIndex} className="field-card">
              <div className="field-header">{field}</div>
              <div className="field-content">
                <table className="match-table">
                  <tbody>
                    {allTimes.map((tKey, idx) => {
                      const match = byTime.get(tKey);
                      if (match) {
                        return <MatchRow key={match.id || `m-${fieldIndex}-${idx}`} match={match} index={idx} />;
                      }
                      return (
                        <EmptyMatchRow
                          key={`empty-${fieldIndex}-${tKey}`}
                          time={tKey.slice(11)}
                        />
                      );
                    })}
                  </tbody>
                </table>
              </div>
            </section>
          );
        })}
      </div>
    </div>
  );
}
